// What the interpreter sends as text, turned into what the subtitle strip and the notes panel
// show. Pure functions over the streams the useTextStream hook collects.
//
// Only streams from the interpreter count: every participant may publish text streams, so a
// stream on these topics from anyone else is ignored (the caller passes the agents' identities).

import { STREAM_ATTRIBUTES, UNCLEAR_TRANSLATION, type SubtitleRole } from './protocol';

/** The parts of a received text stream this module reads (a subset of TextStreamData). */
export interface ReceivedText {
  readonly text: string;
  readonly participantInfo: { readonly identity: string };
  readonly streamInfo: { readonly timestamp: number; readonly attributes?: Record<string, string> | undefined };
}

export interface SubtitleLine {
  readonly segment: string;
  readonly speaker: string;
  readonly sourceLanguage: string;
  readonly original: string | null;
  readonly translations: Readonly<Record<string, string>>;
  readonly final: boolean;
  readonly updatedAt: number;
}

interface MutableLine {
  segment: string;
  speaker: string;
  sourceLanguage: string;
  original: string | null;
  translations: Record<string, string>;
  final: boolean;
  updatedAt: number;
}

/** One line per spoken segment, oldest first. A later stream for the same key replaces the text. */
export function collectSubtitles(streams: readonly ReceivedText[], trustedSenders: ReadonlySet<string>): SubtitleLine[] {
  const lines = new Map<string, MutableLine>();
  for (const stream of streams) {
    if (!trustedSenders.has(stream.participantInfo.identity)) continue;
    const attributes = stream.streamInfo.attributes ?? {};
    const segment = attributes[STREAM_ATTRIBUTES.segment];
    const speaker = attributes[STREAM_ATTRIBUTES.speaker];
    const sourceLanguage = attributes[STREAM_ATTRIBUTES.sourceLanguage];
    const language = attributes[STREAM_ATTRIBUTES.language];
    const role = attributes[STREAM_ATTRIBUTES.role] as SubtitleRole | undefined;
    if (!segment || !speaker || !sourceLanguage || !language) continue;
    if (role !== 'original' && role !== 'translation') continue;

    const line: MutableLine = lines.get(segment) ?? {
      segment,
      speaker,
      sourceLanguage,
      original: null,
      translations: {},
      final: false,
      updatedAt: 0,
    };
    if (role === 'original') line.original = stream.text;
    else line.translations[language] = stream.text;
    line.final = line.final || attributes[STREAM_ATTRIBUTES.final] === 'true';
    line.updatedAt = Math.max(line.updatedAt, stream.streamInfo.timestamp);
    lines.set(segment, line);
  }
  return [...lines.values()].toSorted((a, b) => a.updatedAt - b.updatedAt);
}

/** What the receiver remembers per segment: the sender's stamp last seen, and when it was seen here. */
export interface SeenStamp {
  stamp: number;
  at: number;
}

/**
 * The lines with updatedAt replaced by the time THIS browser last saw them change. The
 * interpreter's stamps come from its own clock, which may be seconds off this device's; the
 * captions' lifetimes count from here. `seen` is the receiver's memory and is updated in place
 * (entries of lines no longer present are dropped).
 */
export function withLocalTimes(lines: readonly SubtitleLine[], seen: Map<string, SeenStamp>, now: number): SubtitleLine[] {
  const present = new Set<string>();
  const result = lines.map((line) => {
    present.add(line.segment);
    const known = seen.get(line.segment);
    if (!known || known.stamp !== line.updatedAt) {
      seen.set(line.segment, { stamp: line.updatedAt, at: now });
      return { ...line, updatedAt: now };
    }
    return { ...line, updatedAt: known.at };
  });
  for (const segment of seen.keys()) {
    if (!present.has(segment)) seen.delete(segment);
  }
  return result;
}

export interface Caption {
  readonly key: string;
  readonly speaker: string;
  readonly sourceLanguage: string;
  /** The translation into the listener's language; null while it is on its way. */
  readonly translation: string | null;
  /** True when the interpreter could not read the sentence (the UNCLEAR marker). */
  readonly unclear: boolean;
  /** What was said, in the speaker's language. */
  readonly original: string | null;
}

/** How long a caption stays once its text is final: a reading pace, within bounds. */
export const CAPTION_MIN_MS = 4000;
export const CAPTION_MAX_MS = 12000;
export const CAPTION_MS_PER_CHAR = 60;
/** A line still being spoken or translated stays this long after its last update. */
export const CAPTION_PENDING_MS = 15000;

function captionLifetime(line: SubtitleLine, translation: string | null): number {
  if (!line.final || translation === null) return CAPTION_PENDING_MS;
  const chars = translation.length + (line.original?.length ?? 0);
  return Math.min(CAPTION_MAX_MS, Math.max(CAPTION_MIN_MS, chars * CAPTION_MS_PER_CHAR));
}

/**
 * The caption on one speaker's tile for one listener: that speaker's latest line, only when
 * the speaker is someone else speaking ANOTHER language (speech in the listener's own
 * language needs no subtitle), and only while it is fresh: a line goes away a reading
 * pace after its text is final.
 */
export function captionOn(
  lines: readonly SubtitleLine[],
  speaker: string,
  listener: { readonly identity: string; readonly language: string },
  now: number,
): Caption | null {
  if (speaker === listener.identity) return null;
  const fresh = (line: SubtitleLine) => now - line.updatedAt <= captionLifetime(line, line.translations[listener.language] ?? null);
  const own = lines.filter((line) => line.speaker === speaker && line.sourceLanguage !== listener.language && fresh(line));
  const newest = own.at(-1);
  if (!newest) return null;
  // While the newest sentence is still on its way, the previous translation stays readable
  // instead of being replaced by "translating": a speaker who keeps talking would otherwise
  // never leave a translation on screen long enough to read.
  const line =
    newest.translations[listener.language] === undefined ? (own.findLast((candidate) => candidate.translations[listener.language] !== undefined) ?? newest) : newest;
  const translation = line.translations[listener.language] ?? null;
  return {
    key: line.segment,
    speaker: line.speaker,
    sourceLanguage: line.sourceLanguage,
    translation: translation === UNCLEAR_TRANSLATION ? null : translation,
    unclear: translation === UNCLEAR_TRANSLATION,
    original: line.original,
  };
}

/** When the newest visible caption among these lines expires, or null when none is visible. */
export function nextCaptionExpiry(lines: readonly SubtitleLine[], listener: { readonly language: string }, now: number): number | null {
  let next: number | null = null;
  for (const line of lines) {
    if (line.sourceLanguage === listener.language) continue;
    const expiry = line.updatedAt + captionLifetime(line, line.translations[listener.language] ?? null);
    if (expiry > now && (next === null || expiry < next)) next = expiry;
  }
  return next;
}

export interface TranscriptEntry {
  readonly key: string;
  readonly speaker: string;
  readonly at: number;
  /** The text in the reader's language: the original when the speaker spoke it, else the translation. */
  readonly text: string | null;
  /** True when the interpreter could not read the sentence. */
  readonly unclear: boolean;
  /** True when the text is the speaker's own words, not a translation. */
  readonly original: boolean;
}

/**
 * The transcript for one reader: every FINAL sentence, oldest first, in the reader's language
 * (the speaker's own words when they spoke that language, the translation otherwise; null
 * while a translation is on its way).
 */
export function transcriptFor(lines: readonly SubtitleLine[], language: string): TranscriptEntry[] {
  const entries: TranscriptEntry[] = [];
  for (const line of lines) {
    if (!line.final) continue;
    const own = line.sourceLanguage === language;
    const text = own ? line.original : (line.translations[language] ?? null);
    entries.push({
      key: line.segment,
      speaker: line.speaker,
      at: line.updatedAt,
      text: text === UNCLEAR_TRANSLATION ? null : text,
      unclear: text === UNCLEAR_TRANSLATION,
      original: own,
    });
  }
  return entries;
}

export interface ActionItem {
  readonly owner: string | null;
  readonly task: string;
  readonly due: string | null;
}

export interface MeetingNotes {
  readonly language: string;
  readonly summary: string;
  readonly decisions: readonly string[];
  readonly actionItems: readonly ActionItem[];
}

const MAX_ITEMS = 200;
const MAX_TEXT = 4000;

function boundedString(value: unknown): value is string {
  return typeof value === 'string' && value.length <= MAX_TEXT;
}

function optionalString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) return null;
  return boundedString(value) ? value : undefined;
}

/**
 * One notes document: JSON {summary, decisions[], action_items[{owner, task, due}]} (the
 * schema in agent/interpreter/notes.py). Null when the text is not that shape.
 */
export function parseNotes(text: string, language: string): MeetingNotes | null {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return null;
  const record = data as Record<string, unknown>;
  const { summary, decisions } = record;
  const items = record.action_items;
  if (!boundedString(summary)) return null;
  if (!Array.isArray(decisions) || decisions.length > MAX_ITEMS || !decisions.every(boundedString)) return null;
  if (!Array.isArray(items) || items.length > MAX_ITEMS) return null;

  const actionItems: ActionItem[] = [];
  for (const item of items) {
    if (typeof item !== 'object' || item === null) return null;
    const entry = item as Record<string, unknown>;
    const owner = optionalString(entry.owner);
    const due = optionalString(entry.due);
    if (!boundedString(entry.task) || owner === undefined || due === undefined) return null;
    actionItems.push({ owner, task: entry.task, due });
  }
  return { language, summary, decisions, actionItems };
}

export interface CopilotItem {
  readonly id: string;
  readonly item: string;
  readonly status: 'open' | 'done';
  readonly note: string | null;
}

export interface CopilotState {
  readonly language: string;
  readonly summary: string;
  readonly checklist: readonly CopilotItem[];
  readonly suggestions: readonly string[];
}

/**
 * One copilot state: JSON {summary, checklist[{id, item, status, note}], suggestions[]} (the
 * schema in agent/interpreter/copilot.py). Null when the text is not that shape.
 */
export function parseCopilot(text: string, language: string): CopilotState | null {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return null;
  const record = data as Record<string, unknown>;
  const { summary, checklist, suggestions } = record;
  if (!boundedString(summary)) return null;
  if (!Array.isArray(suggestions) || suggestions.length > MAX_ITEMS || !suggestions.every(boundedString)) return null;
  if (!Array.isArray(checklist) || checklist.length > MAX_ITEMS) return null;
  const items: CopilotItem[] = [];
  for (const raw of checklist) {
    if (typeof raw !== 'object' || raw === null) return null;
    const entry = raw as Record<string, unknown>;
    const note = optionalString(entry.note);
    if (!boundedString(entry.id) || !boundedString(entry.item) || note === undefined) return null;
    if (entry.status !== 'open' && entry.status !== 'done') return null;
    items.push({ id: entry.id, item: entry.item, status: entry.status, note });
  }
  return { language, summary, checklist: items, suggestions };
}

/** The newest valid copilot state from the interpreter (it is sent to the host alone). */
export function latestCopilot(streams: readonly ReceivedText[], trustedSenders: ReadonlySet<string>): CopilotState | null {
  let newest: { state: CopilotState; at: number } | null = null;
  for (const stream of streams) {
    if (!trustedSenders.has(stream.participantInfo.identity)) continue;
    const attributes = stream.streamInfo.attributes ?? {};
    if (attributes[STREAM_ATTRIBUTES.format] !== 'json') continue;
    const state = parseCopilot(stream.text, attributes[STREAM_ATTRIBUTES.language] ?? '');
    if (!state) continue;
    const at = stream.streamInfo.timestamp;
    if (!newest || at >= newest.at) newest = { state, at };
  }
  return newest?.state ?? null;
}

/** The newest valid notes document from the interpreter, in the listener's language if one came. */
export function latestNotes(
  streams: readonly ReceivedText[],
  trustedSenders: ReadonlySet<string>,
  language: string,
): MeetingNotes | null {
  let newest: { notes: MeetingNotes; at: number } | null = null;
  let newestMine: { notes: MeetingNotes; at: number } | null = null;
  for (const stream of streams) {
    if (!trustedSenders.has(stream.participantInfo.identity)) continue;
    const attributes = stream.streamInfo.attributes ?? {};
    if (attributes[STREAM_ATTRIBUTES.format] !== 'json') continue;
    const notesLanguage = attributes[STREAM_ATTRIBUTES.language] ?? '';
    const notes = parseNotes(stream.text, notesLanguage);
    if (!notes) continue;
    const at = stream.streamInfo.timestamp;
    if (!newest || at >= newest.at) newest = { notes, at };
    if (notesLanguage === language && (!newestMine || at >= newestMine.at)) newestMine = { notes, at };
  }
  return (newestMine ?? newest)?.notes ?? null;
}
